import React, { useEffect } from 'react';
import { GameProvider, useGameContext } from './GameContext';
import ScrambledSentence from './components/ScrambledSentence';
import InputArea from './components/InputArea';
import GameSetup from './components/GameSetup';
import { featureFlags } from './config/featureFlags';
import './App.css';

// Level Info Component
const LevelInfo: React.FC = () => {
  const { 
    currentLevel, 
    totalLevels, 
    levelData, 
    solvedIndices, 
    originalWords 
  } = useGameContext();
  
  return (
    <div className="level-info">
      <div className="level-badge">LEVEL {currentLevel}/{totalLevels}</div>
      {levelData && levelData.difficulty && (
        <div className={`difficulty-badge difficulty-${levelData.difficulty}`}>
          {levelData.difficulty.toUpperCase()}
        </div>
      )}
      {originalWords.length > 0 && (
        <div className="progress-badge">
          {solvedIndices.length}/{originalWords.length}
        </div>
      )}
    </div>
  );
};

const GameContainer: React.FC = () => {
  const { isGameActive, resetGame, refreshDailyLevels, currentLevel, totalLevels } = useGameContext();
  const isHypercard = featureFlags.useHypercardTheme;

  // Apply body class for hypercard theme
  useEffect(() => {
    if (isHypercard) {
      document.body.classList.add('hypercard-body');
    } else {
      document.body.classList.remove('hypercard-body');
    }
    return () => {
      document.body.classList.remove('hypercard-body');
    };
  }, [isHypercard]);

  const handleTitleClick = () => {
    if (isGameActive) {
      resetGame();
    } else {
      // If on start page, refresh the sentences from API
      refreshDailyLevels();
    }
  };

  const containerClass = isHypercard ? 'game-container hypercard' : 'game-container';

  // HyperCard theme layout
  if (isHypercard) {
    return (
      <div className={containerClass}>
        {/* Title bar */}
        <div className="title-bar">
          <div
            className="close-box"
            onClick={handleTitleClick}
            title={isGameActive ? "Return to home page" : "Refresh headlines"}
          />
          <div className="title-bar-stripes"></div>
          <h1 className="app-title">
            <span className="no">NO</span> <span className="way">WAY</span> <span className="lol">LOL</span>
          </h1>
          <div className="title-bar-stripes"></div>
        </div>

        {/* Content */}
        <div className="hypercard-content">
          {isGameActive ? (
            <>
              <LevelInfo />
              <ScrambledSentence />
              <InputArea />
            </>
          ) : (
            <GameSetup />
          )}
        </div>

        {/* Footer */}
        <div className="card-footer">
          <span className="card-number">Card {currentLevel} of {totalLevels || 1}</span>
          <div className="nav-arrows">
            <button className="arrow-btn">◀</button>
            <button className="arrow-btn">▶</button>
          </div>
        </div>
      </div>
    );
  }

  // Classic theme layout
  return (
    <div className={containerClass}>
      <h1
        className="app-title clickable"
        onClick={handleTitleClick}
        title={isGameActive ? "Return to home page" : "Refresh headlines"}
      >
        <span className="no">NO</span> <span className="way">WAY</span> <span className="lol">LOL</span>
      </h1>

      {isGameActive ? (
        <>
          <LevelInfo />
          <ScrambledSentence />
          <InputArea />
        </>
      ) : (
        <GameSetup />
      )}
    </div>
  );
};

const App: React.FC = () => {
  return (
    <GameProvider>
      <GameContainer />
    </GameProvider>
  );
};

export default App;